// src/App.js
import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { AuthProvider } from './context/AuthContext';
import { MessagesProvider } from './context/MessagesContext';
import ProtectedRoute from './components/ProtectedRoute';
import Layout from './components/Layout';
import Login from "./pages/Login";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import Dashboard from "./pages/Dashboard";
import People from './pages/People';
import Messages from './pages/Messages';
import Settings from './pages/Settings';

import AuthCallback from "./pages/AuthCallback";
import VerifyEmail from "./pages/VerifyEmail";
import VerifyEmailChange from "./pages/VerifyEmailChange";
import Training from "./pages/Training";
import Feedback from "./pages/Feedback";
import HelpCenter from "./pages/HelpCenter";
import OnboardingWizard from "./pages/OnboardingWizard";
import SubscriptionExpired from "./pages/SubscriptionExpired";
import Changelog from "./pages/Changelog";


const GA_ID = 'G-43JS5VRZ5T';

// Track page views on client-side route changes
const RouteTracker = () => {
  const location = useLocation();
  React.useEffect(() => {
    if (window.gtag) {
      window.gtag('config', GA_ID, { page_path: location.pathname });
    }
  }, [location]);
  return null;
};

// Create theme
const theme = createTheme({
  palette: {
    primary: {
      main: '#1976d2',
    },
    secondary: {
      main: '#dc004e',
    },
  },
  components: {
    MuiContainer: {
      styleOverrides: {
        root: {
          '@media (max-width: 600px)': {
            paddingLeft: 12,
            paddingRight: 12,
          },
        },
      },
    },
  },
});

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AuthProvider>
        <MessagesProvider>
          <Router>
            <RouteTracker />
            <Routes>
              {/* Public routes */}
              <Route path="/login" element={<Login />} />
              <Route path="/register" element={<Navigate to={process.env.REACT_APP_SIGNUP_URL || "https://get.secondclear.com"} replace />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/auth-callback" element={<AuthCallback />} />
              <Route path="/verify-email" element={<VerifyEmail />} />
              <Route path="/verify-email-change" element={<VerifyEmailChange />} />
              <Route path="/subscription-expired" element={<SubscriptionExpired />} />

              {/* Protected routes */}
              <Route
                path="/dashboard"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <Dashboard />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/people"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <People />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/messages"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <Messages />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/settings"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <Settings />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/training"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <Training />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/feedback"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <Feedback />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/help"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <HelpCenter />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/whats-new"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <Changelog />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/onboarding"
                element={
                  <ProtectedRoute>
                    <OnboardingWizard />
                  </ProtectedRoute>
                }
              />

              {/* Redirect root to dashboard */}
              <Route path="/" element={<Navigate to="/dashboard" replace />} />

              {/* 404 - redirect to dashboard */}
              <Route path="*" element={<Navigate to="/dashboard" replace />} />
            </Routes>
          </Router>
        </MessagesProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
